"use client";

import { SECTION_TITLE } from "@shared/constants/typography";
import { cn } from "@ui/lib";
import { Maximize2 } from "lucide-react";
import { useState } from "react";

export interface ImageToPromptSlide {
	id: string;
	originalImage: { src: string; alt: string };
	extractedPrompt: string;
}

export function ImageToPromptCarousel({
	title,
	description,
	slides,
	labels,
}: {
	title: string;
	description: string;
	slides: ImageToPromptSlide[];
	labels: { original: string; prompt: string };
}) {
	const [zoomedImage, setZoomedImage] = useState<string | null>(null);
	const closeZoom = () => setZoomedImage(null);
	return (
		<>
			{/* Lightbox modal */}
			{zoomedImage && (
				<dialog
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-0 m-0 max-w-none max-h-none w-full h-full"
					onClick={closeZoom}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							closeZoom();
						}
					}}
					open
				>
					<button
						type="button"
						className="absolute top-4 right-4 bg-black/60 text-white p-2 rounded-full hover:bg-black/80 transition-colors"
						onClick={(e) => {
							e.stopPropagation();
							closeZoom();
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.stopPropagation();
								closeZoom();
							}
						}}
						aria-label="Close"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<title>Close</title>
							<path d="M18 6 6 18" />
							<path d="m6 6 12 12" />
						</svg>
					</button>
					<div
						className="max-w-[90vw] max-h-[90vh]"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						<img
							src={zoomedImage}
							alt="Enlarged view of original"
							className="max-w-full max-h-[90vh] object-contain"
						/>
					</div>
				</dialog>
			)}

			<section className="py-12 sm:py-20">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<header className="mb-12 text-center">
						<h2 className={`${SECTION_TITLE.className} mb-4`}>
							{title}
						</h2>
						<p className="mx-auto max-w-2xl text-muted-foreground">
							{description}
						</p>
					</header>
					<div className="space-y-14 sm:space-y-20">
						{slides.map((slide, index) => (
							<article
								key={slide.id}
								className="grid items-center gap-6 md:grid-cols-2 md:gap-12"
							>
								<div
									className={cn(
										"relative mx-auto w-full max-w-md",
										index % 2 === 1 && "md:order-2",
									)}
								>
									<img
										src={slide.originalImage.src}
										alt={slide.originalImage.alt}
										loading="lazy"
										width={768}
										height={1024}
										className="aspect-[3/4] w-full rounded-2xl bg-muted object-contain"
									/>
									<span className="absolute left-3 top-3 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
										{labels.original}
									</span>
									<button
										type="button"
										onClick={() =>
											setZoomedImage(
												slide.originalImage.src,
											)
										}
										aria-label={`Zoom: ${slide.originalImage.alt}`}
										className="absolute bottom-3 right-3 rounded-full bg-black/60 p-2 text-white"
									>
										<Maximize2 className="size-5" />
									</button>
								</div>
								<div className="min-w-0">
									<h3 className="mb-4 text-xl font-semibold">
										{labels.prompt}
									</h3>
									<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-muted-foreground sm:text-base">
										{slide.extractedPrompt}
									</p>
								</div>
							</article>
						))}
					</div>
				</div>
			</section>
		</>
	);
}
